import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { ProposalRequestForm } from '../components/ProposalRequestForm'
import { FileTextIcon } from 'lucide-react'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'

export default function ProposalRequestEdit() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const [request, setRequest] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (id) {
      fetchRequest()
    }
  }, [id])

  const fetchRequest = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('*')
        .eq('id', id)
        .eq('record_type', 'request')
        .single()

      if (error) {
        const { data: converted, error: convertedError } = await supabase
          .from('proposals')
          .select('id, record_type')
          .eq('id', id)
          .single()

        if (!convertedError && converted?.record_type === 'proposal') {
          navigate(`/proposals/view/${id}`, { replace: true })
          return
        }
        throw error
      }
      setRequest(data)
    } catch (error) {
      console.error('Error fetching proposal request:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo))
    } else {
      navigate('/proposals')
    }
  }

  const handleSuccess = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo))
    } else {
      navigate('/proposals')
    }
  }

  const handleCreateProposal = () => {
    if (!request) return
    const returnToUrl = encodeURIComponent(
      `/proposal-requests/edit/${id}${returnTo ? `?returnTo=${returnTo}` : ''}`
    )
    navigate(`/proposals/create?requestId=${request.id}&returnTo=${returnToUrl}`)
  }

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Loading...</p>
      </MobilePage>
    )
  }

  if (!request) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Proposal request not found</p>
      </MobilePage>
    )
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title="Edit Proposal Request"
        onBack={handleClose}
        backLabel={returnTo ? 'Back' : 'Back to Proposals'}
        actions={
          request.status !== 'converted' ? (
            <button
              type="button"
              onClick={handleCreateProposal}
              className={`${mobile.primaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm`}
            >
              <FileTextIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Create Proposal</span>
              <span className="sm:hidden">Proposal</span>
            </button>
          ) : undefined
        }
      />
      <ProposalRequestForm
        onClose={handleClose}
        onSuccess={handleSuccess}
        initialCustomerId={request.customer_id || undefined}
        initialLeadId={request.lead_id || undefined}
        editingRequest={request}
        viewingRequest={undefined}
        isFullPage={true}
      />
    </MobilePage>
  )
}
